"use client"

import * as React from "react"
import {
  ArrowLeftRight,
  Ban,
  BookCheck,
  Circle,
  CircleCheck,
  FilePlus2,
  Landmark,
  PencilLine,
  ReceiptText,
  RotateCcw,
  Send,
  Undo2,
} from "lucide-react"
import { useLocation } from "react-router-dom"

import { FinanceAuditAction, FinancePaymentMethod } from "@ibada/shared-types/finance"
import {
  ReversalTimelineEvent,
  TransferTimelineEvent,
  type EntityInvestigation,
  type InvestigationRef,
  type InvestigationTimelineEvent,
} from "@ibada/shared-types/investigation"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import {
  buildFinanceInvestigationHref,
  type InvestigationContext,
} from "@/features/finance/lib/finance-investigation-url"
import { FINANCE_ACCOUNT_REF_ENTITY } from "@/features/finance/lib/finance-investigation-sheet"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useTranslations } from "@/i18n"

/** Finance's visual language for the timeline: one icon per audit action. */
const TIMELINE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  [FinanceAuditAction.TransactionCreated]: FilePlus2,
  [FinanceAuditAction.TransactionMetadataUpdated]: PencilLine,
  [FinanceAuditAction.TransactionReversed]: Undo2,
  [FinanceAuditAction.TransactionVoided]: Ban,
  [FinanceAuditAction.ReceiptIssued]: ReceiptText,
  [FinanceAuditAction.ReceiptCancelled]: ReceiptText,
  [FinanceAuditAction.ReconciliationClosed]: CircleCheck,
  [FinanceAuditAction.OpeningBalanceInitialized]: Landmark,
  [FinanceAuditAction.PeriodClosed]: BookCheck,
  [FinanceAuditAction.PeriodReopened]: RotateCcw,
  // A transfer's own lifecycle. Without these every transfer step fell through to the bare default dot, so the
  // one part of the timeline that is a decision by a person looked like unlabelled noise.
  [TransferTimelineEvent.Requested]: ArrowLeftRight,
  [TransferTimelineEvent.Submitted]: Send,
  [TransferTimelineEvent.Approved]: CircleCheck,
  [TransferTimelineEvent.Rejected]: Ban,
  [TransferTimelineEvent.Cancelled]: Undo2,
  // The other half of a reversal pair, borrowed onto this timeline so the correction reads as a sequence.
  [ReversalTimelineEvent.OriginalRecorded]: FilePlus2,
  [ReversalTimelineEvent.ReversalPosted]: Undo2,
}

export function financeTimelineIcon(type: string): React.ReactNode {
  const Icon = TIMELINE_ICON[type] ?? Circle
  return <Icon />
}

type Translate = (key: string, values?: Record<string, string | number>) => string

const DETAIL_LABEL_KEYS: Record<string, string> = {
  Type: "fields.type",
  Amount: "fields.amount",
  Currency: "fields.currency",
  Date: "fields.occurredAt",
  Method: "fields.paymentMethod",
  Reference: "fields.reference",
  Description: "fields.description",
  Status: "fields.status",
  Reason: "detail.reason",
}

const SUMMARY_LABEL_KEYS: Record<string, string> = {
  Reference: "fields.reference",
  Reverses: "reversal.reverses",
  "Reversed by": "reversal.reversedBy",
  "Recorded by": "fields.actor",
  Branch: "fields.branch",
  "Sent to": "workspace.labels.sentTo",
  "Received from": "workspace.labels.receivedFrom",
  "Transfer reference": "workspace.labels.transferReference",
  "Transfer status": "workspace.labels.transferStatus",
  "Requested by": "workspace.labels.requestedBy",
  "Approved by": "workspace.labels.approvedBy",
  "Approved at": "workspace.labels.approvedAt",
  "Rejected by": "workspace.labels.rejectedBy",
  "Withdrawn by": "workspace.labels.withdrawnBy",
  "Payment method": "fields.paymentMethod",
  "Payment reference": "fields.paymentReference",
  Reason: "detail.reason",
  Description: "fields.description",
  "Posted at": "workspace.labels.postedAt",
}

const CONTEXT_LABEL_KEYS: Record<string, string> = {
  Type: "fields.type",
  Origin: "workspace.labels.origin",
  Account: "explorer.chips.account",
  Fund: "explorer.chips.fund",
  Program: "workspace.labels.program",
  Activity: "explorer.chips.activity",
  Budget: "workspace.labels.budget",
  "Sent to": "workspace.labels.sentTo",
  "Received from": "workspace.labels.receivedFrom",
}

const STATUS_KEYS: Record<string, string> = {
  ACTIVE: "status.ACTIVE",
  Active: "status.ACTIVE",
  CANCELLED: "status.CANCELLED",
  Reversed: "status.CANCELLED",
  VOIDED: "status.VOIDED",
  Voided: "status.VOIDED",
  POSTED: "workspace.status.POSTED",
  Posted: "workspace.status.POSTED",
  REVERSED: "workspace.status.REVERSED",
  REVERSAL: "workspace.status.reversal",
  Reversal: "workspace.status.reversal",
}

const TYPE_KEYS: Record<string, string> = {
  INCOME: "type.INCOME",
  Income: "type.INCOME",
  EXPENSE: "type.EXPENSE",
  Expense: "type.EXPENSE",
  TRANSFER: "type.TRANSFER",
  Transfer: "type.TRANSFER",
  TRANSFER_IN: "type.TRANSFER_IN",
  "Transfer in": "type.TRANSFER_IN",
  TRANSFER_OUT: "type.TRANSFER_OUT",
  "Transfer out": "type.TRANSFER_OUT",
}

const BALANCE_KEYS: Record<string, string> = {
  BALANCED: "journal.balanced",
  Balanced: "journal.balanced",
  UNBALANCED: "journal.unbalanced",
  Unbalanced: "journal.unbalanced",
}

const LEDGER_SOURCE_KEYS: Record<string, string> = {
  "Opening balance": "workspace.labels.openingBalance",
  "Opening balance correction": "workspace.labels.openingBalanceCorrection",
  "Internal transfer": "workspace.labels.internalTransfer",
}

/** Every payment method, in words. Typed on the enum so a new method cannot reach the page as a raw code. */
const PAYMENT_METHOD_LABEL_KEYS: Record<FinancePaymentMethod, string> = {
  [FinancePaymentMethod.Cash]: "workspace.paymentMethod.CASH",
  [FinancePaymentMethod.BankTransfer]: "workspace.paymentMethod.BANK_TRANSFER",
  [FinancePaymentMethod.MobileMoney]: "workspace.paymentMethod.MOBILE_MONEY",
  [FinancePaymentMethod.Card]: "workspace.paymentMethod.CARD",
  [FinancePaymentMethod.Check]: "workspace.paymentMethod.CHECK",
  [FinancePaymentMethod.Other]: "workspace.paymentMethod.OTHER",
}

const PAYMENT_METHOD_KEYS: Record<string, string> = PAYMENT_METHOD_LABEL_KEYS

const isPaymentMethod = (value: string): value is FinancePaymentMethod =>
  (Object.values(FinancePaymentMethod) as string[]).includes(value)

/** A payment method in the reader's words (`finance.audit` namespace), or null when none was recorded. */
export function paymentMethodLabel(method: string | null | undefined, t: Translate): string | null {
  if (!method) return null
  return isPaymentMethod(method) ? t(PAYMENT_METHOD_LABEL_KEYS[method]) : method
}

const AUDIT_ACTION_TITLE_KEYS: Partial<Record<string, string>> = {
  [FinanceAuditAction.TransactionCreated]: "actions.TRANSACTION_CREATED",
  [FinanceAuditAction.TransactionMetadataUpdated]: "actions.TRANSACTION_METADATA_UPDATED",
  [FinanceAuditAction.TransactionReversed]: "actions.TRANSACTION_REVERSED",
  [FinanceAuditAction.TransactionVoided]: "actions.TRANSACTION_VOIDED",
  [FinanceAuditAction.ReceiptIssued]: "actions.RECEIPT_ISSUED",
  [FinanceAuditAction.ReceiptCancelled]: "actions.RECEIPT_CANCELLED",
  [FinanceAuditAction.ReconciliationClosed]: "actions.RECONCILIATION_CLOSED",
  [FinanceAuditAction.OpeningBalanceInitialized]: "actions.OPENING_BALANCE_INITIALIZED",
  [FinanceAuditAction.PeriodClosed]: "actions.PERIOD_CLOSED",
  [FinanceAuditAction.PeriodReopened]: "actions.PERIOD_REOPENED",
  [FinanceAuditAction.FundCreated]: "actions.FUND_CREATED",
  [FinanceAuditAction.FundUpdated]: "actions.FUND_UPDATED",
  [FinanceAuditAction.FundClosed]: "actions.FUND_CLOSED",
  [FinanceAuditAction.FundReallocated]: "actions.FUND_REALLOCATED",
  [FinanceAuditAction.BudgetApproved]: "actions.BUDGET_APPROVED",
  [FinanceAuditAction.BudgetRevised]: "actions.BUDGET_REVISED",
  [FinanceAuditAction.BudgetLineLinked]: "actions.BUDGET_LINE_LINKED",
  [FinanceAuditAction.ReconciliationSubmitted]: "actions.RECONCILIATION_SUBMITTED",
  [FinanceAuditAction.ReconciliationApproved]: "actions.RECONCILIATION_APPROVED",
  [FinanceAuditAction.ReconciliationRejected]: "actions.RECONCILIATION_REJECTED",
  [FinanceAuditAction.ReconciliationWithdrawn]: "actions.RECONCILIATION_WITHDRAWN",
  [FinanceAuditAction.AccountCreated]: "actions.ACCOUNT_CREATED",
  [FinanceAuditAction.AccountUpdated]: "actions.ACCOUNT_UPDATED",
  [FinanceAuditAction.AccountArchived]: "actions.ACCOUNT_ARCHIVED",
  [FinanceAuditAction.FinanceSetupCompleted]: "actions.FINANCE_SETUP_COMPLETED",
  [FinanceAuditAction.FinanceActivated]: "actions.FINANCE_ACTIVATED",
  [FinanceAuditAction.FinanceGovernanceSet]: "actions.FINANCE_GOVERNANCE_SET",
  [FinanceAuditAction.OpeningFundAllocationSet]: "actions.OPENING_FUND_ALLOCATION_SET",
  [FinanceAuditAction.TransferSubmitted]: "actions.TRANSFER_SUBMITTED",
  [FinanceAuditAction.TransferApproved]: "actions.TRANSFER_APPROVED",
  [FinanceAuditAction.TransferRejected]: "actions.TRANSFER_REJECTED",
  [FinanceAuditAction.TransferCancelled]: "actions.TRANSFER_CANCELLED",
  [FinanceAuditAction.TransferReversed]: "actions.TRANSFER_REVERSED",
}

function knownKey(map: Record<string, string>, value: string) {
  const normalized = value.trim()
  return map[value] ?? map[normalized] ?? map[normalized.toUpperCase()] ?? null
}

function translateKnown(map: Record<string, string>, value: string | null | undefined, t: Translate) {
  if (!value) return value ?? null
  const key = knownKey(map, value)
  return key ? t(key) : value
}

/** Like {@link translateKnown}, but null on a miss, so several maps can be tried in turn. */
function translateIfKnown(map: Record<string, string>, value: string, t: Translate): string | null {
  const key = knownKey(map, value)
  return key ? t(key) : null
}

/** A ref's label arrives already localized by the API (ADR-0030); only absence is normalized here. */
function localizeRef(ref: InvestigationRef | null | undefined) {
  return ref ?? null
}

function localizeDateLikeValue(value: string, formatDateTime: (iso: string) => string) {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : formatDateTime(parsed.toISOString())
}

function localizeDetailValue(
  label: string,
  value: string,
  t: Translate,
  formatDateTime: (iso: string) => string,
  formatAmount?: (amount: string) => string,
): string {
  if (value.includes(" → ")) {
    return value
      .split(" → ")
      .map((part) => localizeDetailValue(label, part.trim(), t, formatDateTime, formatAmount))
      .join(" → ")
  }

  // An empty side of a change (a value set for the first time, or cleared): said in words, never a dash glyph.
  if (value === "") return t("sheet.history.empty")
  if (label === "Amount" && formatAmount && /^-?\d+(\.\d+)?$/.test(value)) return formatAmount(value)
  if (label === "Type") return translateKnown(TYPE_KEYS, value, t) ?? value
  if (label === "Status") return translateKnown(STATUS_KEYS, value, t) ?? value
  if (label === "Method") return translateKnown(PAYMENT_METHOD_KEYS, value, t) ?? value
  if (label === "Date") return localizeDateLikeValue(value, formatDateTime)
  return value
}

function localizeTimelineTitle(event: InvestigationTimelineEvent, t: Translate) {
  if (event.title.startsWith("Approved by ")) {
    return t("workspace.timeline.approvedBy", { branch: event.title.replace("Approved by ", "") })
  }
  if (event.title.startsWith("Reverses ")) {
    return t("workspace.timeline.reverses", { reference: event.title.replace("Reverses ", "") })
  }
  if (event.title.startsWith("Reversed by ")) {
    return t("workspace.timeline.reversedBy", { reference: event.title.replace("Reversed by ", "") })
  }

  const [base, suffix] = event.title.split(" · ")
  const key = AUDIT_ACTION_TITLE_KEYS[event.type]
  const title = key ? t(key) : translateKnown({
    "Transfer requested": "workspace.timeline.transferRequested",
    "Sent for approval": "workspace.timeline.sentForApproval",
    Approved: "workspace.timeline.approved",
    Rejected: "workspace.timeline.rejected",
    Withdrawn: "workspace.timeline.withdrawn",
    "Posted to the ledger": "workspace.timeline.postedToLedger",
  }, base, t) ?? base
  return suffix ? `${title} · ${suffix}` : title
}

function localizeBadgeLabel(label: string, t: Translate) {
  const [baseLabel, suffixLabel] = label.split(" · ")
  const base = baseLabel.trim()
  const suffix = suffixLabel?.trim()
  // Each map is tried in turn. `translateKnown` echoes the input on a miss, so chaining it stopped at the first
  // map and left "Active" and "Balanced" in English on a French page.
  const translated = translateIfKnown(TYPE_KEYS, base, t)
    ?? translateIfKnown(STATUS_KEYS, base, t)
    ?? translateIfKnown(BALANCE_KEYS, base, t)
    ?? translateIfKnown(LEDGER_SOURCE_KEYS, base, t)
    ?? base
  return suffix ? `${translated} · ${suffix}` : translated
}

function localizeContextValue(label: string, value: string, t: Translate) {
  if (label === "Type") return translateKnown(TYPE_KEYS, value, t) ?? value
  if (label === "Origin") return translateKnown(LEDGER_SOURCE_KEYS, value, t) ?? value
  return value
}

function localizeInvestigation(
  investigation: EntityInvestigation,
  t: Translate,
  formatDateTime: (iso: string) => string,
  formatAmount?: (amount: string) => string,
): EntityInvestigation {
  return {
    ...investigation,
    header: {
      ...investigation.header,
      // A ledger entry is titled by its origin ("Opening balance"); a transaction by its reference, left as is.
      title: translateKnown(LEDGER_SOURCE_KEYS, investigation.header.title, t) ?? investigation.header.title,
      subtitle: investigation.header.subtitle?.startsWith("Reverses ")
        ? t("workspace.timeline.reverses", { reference: investigation.header.subtitle.replace("Reverses ", "") })
        : investigation.header.subtitle,
      badges: investigation.header.badges.map((badge) => ({ ...badge, label: localizeBadgeLabel(badge.label, t) })),
    },
    summary: investigation.summary.map((field) => ({
      ...field,
      label: translateKnown(SUMMARY_LABEL_KEYS, field.label, t) ?? field.label,
      value:
        field.label === "Transfer status"
          ? translateKnown(STATUS_KEYS, field.value ?? null, t)
          : field.label === "Approved at" || field.label === "Posted at"
            ? field.value
              ? localizeDateLikeValue(field.value, formatDateTime)
              : field.value
            : field.value,
      ref: localizeRef(field.ref),
    })),
    timeline: investigation.timeline.map((event) => ({
      ...event,
      title: localizeTimelineTitle(event, t),
      actor: event.actor ?? null,
      details: event.details?.map((detail) => ({
        label: translateKnown(DETAIL_LABEL_KEYS, detail.label, t) ?? detail.label,
        value: localizeDetailValue(detail.label, detail.value, t, formatDateTime, formatAmount),
      })),
      ref: localizeRef(event.ref),
    })),
    accountingImpact: investigation.accountingImpact
      ? {
          ...investigation.accountingImpact,
          status:
            translateKnown(STATUS_KEYS, investigation.accountingImpact.status, t) ??
            investigation.accountingImpact.status,
          lines: investigation.accountingImpact.lines.map((line) => ({
            ...line,
            name: line.name,
          })),
        }
      : investigation.accountingImpact,
    businessContext: investigation.businessContext?.map((step) => ({
      ...step,
      label: translateKnown(CONTEXT_LABEL_KEYS, step.label, t) ?? step.label,
      value: step.ref
        ? step.value
        : localizeContextValue(step.label, step.value, t),
      ref: localizeRef(step.ref),
    })),
    evidence: investigation.evidence?.map((item) => ({
      ...item,
      status: item.status ? (translateKnown(STATUS_KEYS, item.status, t) ?? item.status) : item.status,
      ref: localizeRef(item.ref),
    })),
    related: investigation.related.map((ref) => localizeRef(ref) ?? ref),
  }
}

export function useLocalizedFinanceInvestigation<T extends EntityInvestigation>(
  investigation: T | null | undefined,
  formatDateTime: (iso: string) => string,
  formatAmount?: (amount: string) => string,
): T | null | undefined {
  const t = useTranslations("finance.audit")
  return React.useMemo(
    () =>
      investigation
        ? { ...investigation, ...localizeInvestigation(investigation, t, formatDateTime, formatAmount) }
        : investigation,
    [formatAmount, formatDateTime, investigation, t],
  )
}

/** Map a related ref to the finance investigation URL contract. */
function refToContext(ref: InvestigationRef): InvestigationContext {
  const label = ref.label
  switch (ref.entity) {
    case "transaction":
      return { transactionId: ref.id }
    case "journal":
      return { journalEntryId: ref.id }
    case "account":
    case "accountingAccount":
      return { accountingAccountId: ref.id, label }
    case "fund":
      return { fundId: ref.id, label }
    case "activity":
      return { activityId: ref.id, label }
    case "budgetLine":
      return { budgetLineId: ref.id, label }
    case "incomeCategory":
      return { incomeCategoryId: ref.id, label }
    case "expenseCategory":
      return { expenseCategoryId: ref.id, label }
    case "program":
      return { programId: ref.id, label }
    case "member":
      return { memberId: ref.id, label }
    case "fiscalPeriod":
      // The date-labelled period pill opens the period's transactions (Explorer, date-filtered), not the bare
      // landing page (IBA-139). Bounds ride on the ref from the backend.
      return { dateFrom: ref.dateFrom ?? undefined, dateTo: ref.dateTo ?? undefined, label }
    default:
      return { label }
  }
}

/** Resolve any investigation ref to a finance href — so the workspace is a graph you can walk. */
export function useFinanceInvestigationResolveHref(): (ref: InvestigationRef) => string {
  const workspaceSlug = useActiveWorkspaceSlug()
  const branchSlug = useActiveBranchSlug()
  const { search } = useLocation()
  const financeContext =
    new URLSearchParams(search).get("context") === "parish" ? ("parish" as const) : ("branch" as const)

  return React.useCallback(
    (ref: InvestigationRef) =>
      // A treasury account has its own page (overview + reconciliation), not an Explorer facet.
      ref.entity === FINANCE_ACCOUNT_REF_ENTITY
        ? buildBranchAdminUrl(
            branchSlug,
            `/finance/accounts/${ref.id}${financeContext === "parish" ? "?context=parish" : ""}`,
            workspaceSlug,
          )
        : buildFinanceInvestigationHref(refToContext(ref), { workspaceSlug, branchSlug, financeContext }),
    [workspaceSlug, branchSlug, financeContext],
  )
}
